import React, { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";

/* ================= CONFIG ================= */

const API_URL = "https://neusoftbackend.onrender.com/api";
const LOGO_URL =
  "https://www.neusoft.com/wp-content/themes/neusoft-en/assets/img/logo.svg";

/* ================= UI COMPONENTS ================= */

const Navbar = ({ user }) => (
  <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-8 py-5 flex justify-between items-center">
      <div className="flex items-center gap-4">
        <img src={LOGO_URL} alt="Logo" className="h-7" />
        <span className="text-xs tracking-[0.35em] font-semibold text-gray-700">
          SECURE EXAMINATION PORTAL
        </span>
      </div>
      {user && (
        <div className="text-xs text-gray-500 tracking-widest">
          CANDIDATE ID: <span className="font-semibold">{user.username}</span>
        </div>
      )}
    </div>
  </nav>
);

const SectionCard = ({ title, children }) => (
  <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
    <h3 className="text-sm font-semibold tracking-widest text-gray-800 mb-4 uppercase">
      {title}
    </h3>
    <div className="text-xs text-gray-600 leading-relaxed space-y-2">
      {children}
    </div>
  </div>
);

const MicVisualizer = () => (
  <div className="flex items-end gap-[2px] h-3">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="w-[2px] bg-white animate-pulse"
        style={{ height: `${30 + Math.random() * 60}%` }}
      />
    ))}
  </div>
);

/* ================= APP ================= */
export default function App() {
  const [page, setPage] = useState("login");
  const [isLogin, setIsLogin] = useState(true);
  const [user, setUser] = useState(null);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    code: "", // Added for Verification Code
  });

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [examResult, setExamResult] = useState(null);

  const [hasPermissions, setHasPermissions] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");

  const [countdown, setCountdown] = useState(5);
  const [warning, setWarning] = useState("");

  const webcamRef = useRef(null);

  /* ================= PERMISSIONS ================= */

  const requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      stream.getTracks().forEach((t) => t.stop());
      setHasPermissions(true);
    } catch {
      alert("Camera or microphone not available.");
    }
  };

  /* ================= AUTH ================= */

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const endpoint = isLogin ? "/login" : "/register";
      const res = await fetch(API_URL + endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          isLogin
            ? { email: formData.email, password: formData.password }
            : formData
        ),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      // --- VERIFICATION CODE CHECK ---
      if (isLogin && data.user.verificationCode !== formData.code) {
        throw new Error("Invalid Verification Code.");
      }

      // --- TIMER START WINDOW CHECK (30 MINS) ---
      if (isLogin && data.user.scheduledAt) {
        const scheduledTime = new Date(data.user.scheduledAt).getTime();
        const currentTime = new Date().getTime();
        const diffMinutes = (scheduledTime - currentTime) / (1000 * 60);

        if (diffMinutes > 30) {
          throw new Error(`Access Denied. Entry permitted 30 mins before start (${new Date(data.user.scheduledAt).toLocaleTimeString()}).`);
        }
      }

      setUser(data.user);
      setPage("instructions");
    } catch (e) {
      setError(e.message);
    }
  };

  const submitExam = async () => {
    try {
      const payload = Object.entries(answers).map(([id, opt]) => ({
        questionId: Number(id),
        selectedOption: opt,
      }));

      const res = await fetch(`${API_URL}/submit-exam`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          username: user.username,
          answers: payload,
        }),
      });

      const data = await res.json();
      setExamResult(data);
      setPage("results");
    } catch {
      alert("Submission failed. Please contact administrator.");
    }
  };

  /* ================= EXAM START ================= */

  const startExamWithLock = async () => {
    await document.documentElement.requestFullscreen();
    setPage("countdown");

    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c === 1) {
          clearInterval(timer);
          startExam();
        }
        return c - 1;
      });
    }, 1000);
  };

  const startExam = async () => {
    const res = await fetch(`${API_URL}/questions`);
    const data = await res.json();
    setQuestions(data);
    setPage("exam");
  };

  /* ================= TAB SWITCH DETECTION ================= */

  useEffect(() => {
    const onVisibility = () => {
      if (page === "exam" && document.hidden) {
        alert("Tab switch detected. Exam terminated.");
        window.location.reload();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [page]);

  /* ================= FACE CHECK ================= */

  useEffect(() => {
    if (page !== "exam") return;

    const interval = setInterval(() => {
      const video = webcamRef.current?.video;
      if (!video || video.videoWidth === 0) {
        setWarning("Face not detected. Please stay in front of the camera.");
      } else {
        setWarning("");
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [page]);

  /* ================= LOGIN ================= */

  if (page === "login") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200">
        <Navbar />

        <div className="flex items-center justify-center h-[85vh]">
          <form
            onSubmit={handleAuth}
            className="bg-white rounded-2xl shadow-xl p-12 w-[380px] space-y-8"
          >
            {/* LOGO */}
            <div className="flex flex-col items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Neusoft Logo" className="h-10" />
              <p className="text-[10px] tracking-[0.35em] text-gray-500">
                SECURE EXAMINATION PORTAL
              </p>
            </div>

            <h2 className="text-center text-sm tracking-[0.3em] font-semibold">
              {isLogin ? "CANDIDATE LOGIN" : "CANDIDATE REGISTRATION"}
            </h2>

            {!isLogin && (
              <input
                placeholder="FULL NAME"
                required
                className="w-full border-b py-3 text-xs outline-none"
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
              />
            )}

            <input
              placeholder="EMAIL ADDRESS"
              required
              className="w-full border-b py-3 text-xs outline-none"
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />

            <input
              type="password"
              placeholder="PASSWORD"
              required
              className="w-full border-b py-3 text-xs outline-none"
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />

            {/* VERIFICATION CODE INPUT */}
            {isLogin && (
              <input
                type="text"
                placeholder="VERIFICATION CODE (6-DIGITS)"
                required
                maxLength="6"
                className="w-full border-b py-3 text-xs outline-none font-mono tracking-widest"
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
              />
            )}

            {error && (
              <p className="text-red-600 text-[11px] text-center">{error}</p>
            )}

            <button className="w-full bg-black text-white py-4 text-xs tracking-widest rounded-lg">
              CONTINUE
            </button>

            <p
              className="text-[10px] text-center underline cursor-pointer"
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? "Create new account" : "Back to login"}
            </p>
          </form>
        </div>
      </div>
    );
  }

  /* ================= INSTRUCTIONS ================= */

  if (page === "instructions") {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} />

        <div className="max-w-7xl mx-auto px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* LEFT – INSTRUCTIONS DOCUMENT */}
          <div className="lg:col-span-2 h-[75vh] overflow-y-auto pr-6 border-r">
            <h1 className="text-2xl font-semibold mb-8">
              Examination Instructions, Rules & Terms
            </h1>
            {/* Show scheduled time if available */}
            {user.scheduledAt && (
              <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 text-xs">
                <strong>SCHEDULED START:</strong>{" "}
                {new Date(user.scheduledAt).toLocaleString()}
              </div>
            )}
            <section className="mb-10 text-sm leading-relaxed">
              <p>
                Please read all instructions carefully before starting the
                examination. Non-compliance may result in immediate termination.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-semibold mb-2">1. Examination Guidelines</h2>
              <ul className="list-disc ml-6 space-y-2 text-sm">
                <li>The exam is time-bound and auto-submits on timeout.</li>
                <li>One correct answer per question.</li>
                <li>Navigation is allowed until final submission.</li>
                <li>Unanswered questions are marked incorrect.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-semibold mb-2">2. Proctoring Policy</h2>
              <ul className="list-disc ml-6 space-y-2 text-sm">
                <li>Webcam and microphone must remain enabled.</li>
                <li>Face must be visible and centered at all times.</li>
                <li>No additional people or voices allowed.</li>
                <li>Camera obstruction or absence is prohibited.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-semibold mb-2">3. Browser & Device Rules</h2>
              <ul className="list-disc ml-6 space-y-2 text-sm">
                <li>Fullscreen mode is mandatory.</li>
                <li>Tab switching or minimizing terminates the exam.</li>
                <li>No external devices, screen recording, or screenshots.</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="font-semibold mb-2">4. Declaration</h2>
              <p className="text-sm">
                By proceeding, you consent to audio-visual monitoring and agree
                to all rules stated above.
              </p>
            </section>
          </div>

          {/* RIGHT – PROCTOR PANEL */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 border border-gray-300 rounded-lg p-4 shadow-sm bg-white">
              {/* CAMERA PREVIEW */}
              <div className="aspect-video bg-black rounded overflow-hidden mb-4">
                {hasPermissions ? (
                  <Webcam audio muted className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-white text-xs">
                    Camera Preview
                  </div>
                )}
              </div>

              {/* COUNTDOWN */}
              <div className="text-center mb-4">
                <p className="text-[10px] tracking-widest text-gray-500">
                  EXAM BEGINS IN
                </p>
                <p className="text-3xl font-semibold">{countdown}s</p>
              </div>

              {/* CONSENT */}
              <label className="flex gap-2 text-xs mb-4">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={() => setAcceptedTerms(!acceptedTerms)}
                />
                <span>
                  I confirm that I have read and agree to the examination rules.
                </span>
              </label>

              {/* ACTION BUTTON */}
              {!hasPermissions ? (
                <button
                  disabled={!acceptedTerms}
                  onClick={requestPermissions}
                  className="w-full border border-black py-3 text-xs tracking-widest disabled:opacity-40"
                >
                  INITIALIZE CAMERA & MICROPHONE
                </button>
              ) : (
                <button
                  onClick={startExamWithLock}
                  className="w-full bg-black text-white py-3 text-xs tracking-widest"
                >
                  START EXAM
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ================= COUNTDOWN ================= */

  if (page === "countdown") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <p className="tracking-widest mb-4 text-xs">EXAM STARTING IN</p>
          <div className="text-7xl">{countdown}</div>
        </div>
      </div>
    );
  }

  /* ================= EXAM ================= */

  if (page === "exam") {
    const q = questions[currentQuestion];

    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} />

        <div className="fixed top-24 right-8 w-56 z-50">
          <Webcam ref={webcamRef} audio muted />
          <div className="bg-black text-white px-3 py-2 text-[10px] flex justify-between">
            <span>LIVE PROCTORING</span>
            <MicVisualizer />
          </div>
        </div>

        {warning && (
          <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-2 text-xs rounded">
            {warning}
          </div>
        )}

        <main className="max-w-4xl mx-auto px-8 py-20">
          <h2 className="mb-10">{q?.question}</h2>

          {q?.options.map((opt, i) => (
            <button
              key={i}
              onClick={() => setAnswers({ ...answers, [q.id]: i })}
              className={`block w-full mb-4 p-4 border rounded ${
                answers[q.id] === i ? "bg-black text-white" : "hover:border-black"
              }`}
            >
              {opt}
            </button>
          ))}

          <div className="flex justify-between mt-12">
            <button
              disabled={currentQuestion === 0}
              onClick={() => setCurrentQuestion((c) => c - 1)}
            >
              Previous
            </button>

            {currentQuestion === questions.length - 1 ? (
              <button onClick={submitExam}>Submit</button>
            ) : (
              <button onClick={() => setCurrentQuestion((c) => c + 1)}>
                Next
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  return null;
}